import { Typography, Button, Form, Tabs, Alert } from 'antd';
import React, { forwardRef, useCallback, useEffect, useState, useMemo, useRef } from 'react';
import ProductAPI from 'restAPI/ProductAPI';
import BaseModal from 'components/BaseModal';
import { PlusOutlined, ArrowDownOutlined } from '@ant-design/icons';

import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { MODAL_SIZE_LARGE } from 'constants/Layout';
import Vocabulary from 'components/Vocabulary';
import { FieldTypes } from 'utils/formUtils';
import message from "components/CustomMessage";
import SubProductAPI from 'restAPI/SubProductAPI';
import SubProductEnvironmentTypeAPI from 'restAPI/SubProductEnvironmentTypeAPI';
import { capitalize, filter, find, forEach, isArray, isEmpty, map, uniqBy, isNull, isUndefined } from 'lodash';
import { DeleteIcon } from 'components/Icons';
import classNames from 'classnames';
import { generateFormFields } from 'utils/formUtils';
import { IntegrationType } from '../common';
import { findOptionsFromMap } from '../GenericMappingForm';
import { flatMapDeep } from 'lodash';
import _ from 'lodash';

const { TabPane } = Tabs

const initialFormValues = {
    configurations: [{
        product: undefined,
        subProduct: undefined,
        environment: undefined,
    }]
}

const commonTwistLockFieldsProperty = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
}

const TwistLockMappingFormV2 = forwardRef(function TwistLockMappingFormV2({disableProjectFields = false,...props}, ref) {
    const dataRef = useRef();
    const [form] = Form.useForm(props.form);
    const editMode = !isEmpty(props.record);
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [subProductsMap, setSubProductsMap] = useState({});
    const [envMap, setEnvMap] = useState({});
    const disableMapping = editMode && !props.isUnmapped;

    if (props.record) {
        const {
          imageId,
          imageName,
          hostId,
          hostName,
          registryId,
          registryName,
          ciImageId,
          ciImageName,
          codeRepoId,
          codeRepoName,
          codeRepositoryId,
          codeRepositoryName,
        } = props.record;
    
        const _dataRef = {};
    
        if (imageId) {
          const imageIdArray = _.split(imageId, ",");
          const imageNameArray = _.split(imageName, ",");
          _dataRef.project = {
            ..._dataRef.project,
            image: _.map(imageIdArray, (id, index) => ({
              value: id,
              label: imageNameArray[index],
            })),
          };
        }
    
        if (hostId) {
          const hostIdArray = _.split(hostId, ",");
          const hostNameArray = _.split(hostName, ",");
          _dataRef.project = {
            ..._dataRef.project,
            host: _.map(hostIdArray, (id, index) => ({
              value: id,
              label: hostNameArray[index],
            })),
          };
        }
    
        if (registryId) {
          const registryIdArray = _.split(registryId, ",");
          const registryNameArray = _.split(registryName, ",");
          _dataRef.project = {
            ..._dataRef.project,
            registry: _.map(registryIdArray, (id, index) => ({
              value: id,
              label: registryNameArray[index],
            })),
          };
        }
    
        if (ciImageId) {
          const ciImageIdArray = _.split(ciImageId, ",");
          const ciImageNameArray = _.split(ciImageName, ",");
          _dataRef.project = {
            ..._dataRef.project,
            ci: _.map(ciImageIdArray, (id, index) => ({
              value: id,
              label: ciImageNameArray[index],
            })),
          };
        }
    
        if (codeRepoId) {
          const codeRepoIdArray = _.split(codeRepoId, ",");
          const codeRepoNameArray = _.split(codeRepoName, ",");
          _dataRef.project = {
            ..._dataRef.project,
            code: _.map(codeRepoIdArray, (id, index) => ({
              value: id,
              label: codeRepoNameArray[index],
            })),
          };
        }
        if (codeRepositoryId) {
          const codeRepositoryIdArray = _.split(codeRepositoryId, ",");
          const codeRepositoryNameArray = _.split(codeRepositoryName, ",");
          _dataRef.project = {
            ..._dataRef.project,
            codeRepo: _.map(codeRepositoryIdArray, (id, index) => ({
              value: id,
              label: codeRepositoryNameArray[index],
            })),
          };
        }
        dataRef.current = {..._dataRef};
    }

    const setOptionsFromPagination =  useCallback((apiOptions,key) =>  {
        const parseOption = parseGroupSelectOption(apiOptions,key);
        dataRef.current = { 
            clusters: uniqBy([...(dataRef.current?.clusters || [] ),...parseOption.clusters], 'value'),
            project: {...(dataRef.current?.project || []),[key]: uniqBy([...(dataRef.current?.project?.[key] || []),...parseOption.project[key]], 'value')},
        }
         
    },[])

    const parseExtraFieldsDataFromResponse = useCallback((config) => {
        const  { imageId, hostId, registryId, clusterName, ciImageName, hostClusterName,codeRepoId, codeRepositoryId } = config;
        const isAllHosts = hostId === "ALL-HOSTS";
        const image = isEmpty(imageId) ? [] : imageId?.split(',')
        const host = isAllHosts || isEmpty(hostId) ?  [] : hostId?.split(',')
        const registry = isEmpty(registryId) ?  [] : registryId?.split(',')
        const ci = isEmpty(ciImageName) ? [] : ciImageName.split(',')
        const code = isEmpty(codeRepoId) ? [] : codeRepoId.split(',')
        const cluster = isEmpty(clusterName) ? null : clusterName
        const hostCluster= isEmpty(hostClusterName) ? null : hostClusterName
        const codeRepository = isEmpty(codeRepositoryId) ? [] : codeRepositoryId.split(',');

        return {
            imageId:image,
            hostId:host, 
            registryId:registry,
            clusterName:cluster,
            ciId:ci,
            hostClusterName:hostCluster,
            codeRepoId:code,
            codeRepositoryId: codeRepository,
            allHosts: isAllHosts,
            //send the property received in config
          ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
        };
      }, []);

      
    const getExtraFieldsData = (config) => {
        const { clusterName, hostId, imageId, registryId, ciId, hostClusterName,codeRepoId,imageName,ciImageName,codeRepoName,hostName,registryName, codeRepositoryId, codeRepositoryName, allHosts } = config;
    
        if(isEmpty(imageId) && (!allHosts && isEmpty(hostId)) && isEmpty(registryId) && isEmpty(ciId) && isEmpty(codeRepoId) && isEmpty(codeRepositoryId)){
          throw new Error("Please select atleast one Host/Image/Registry");
        }

        if(allHosts && _.isUndefined(hostClusterName)){
            throw new Error("Please select a Host Cluster to select All Hosts")
        }
    
        const imageNameData = [];
        const hostNameData = [];
        const registryNameData = [];
        const ciNameData = [];
        const codeRepoData = [];
        const codeRepositoryData = [];
         Object.keys(dataRef.current.project).forEach((parent) =>{
          forEach(dataRef.current.project[parent], project => {
              switch(parent){
                case 'image':
                  if( imageId && imageId.includes(project.value)) {
                    imageNameData.push(project.label)
                  }
                  break;
                case 'host':
                  if( hostId && hostId.includes(project.value)) {
                    hostNameData.push(project.label)
                  }
                  break;
                case 'registry' :
                  if( registryId && registryId.includes(project.value)) {
                    registryNameData.push(project.label)
                  }
                  break;
                case 'ci' :
                  if( ciId && ciId.includes(project.value)) {
                    ciNameData.push(project.label)
                  }
                break;
                case 'code' :
                  if( codeRepoId && codeRepoId.includes(project.value)) {
                    codeRepoData.push(project.label)
                  }
                break;
                case 'codeRepo' :
                  if( codeRepositoryId && codeRepositoryId.includes(project.value)) {
                    codeRepositoryData.push(project.label)
                  }
                break;
                default:
                  return;
              }
          });
         })
    
        return ({
          imageId: imageId?.join() || '',
          imageName: !isEmpty(imageId) ? imageNameData?.join() || imageName : '',
          hostId: allHosts ? "ALL-HOSTS"  : hostId?.join() || '',
          hostName: allHosts ? "ALL-HOSTS" : !isEmpty(hostId) ? hostNameData?.join() || hostName : '',
          registryId: registryId?.join() || '',
          registryName: !isEmpty(registryId) ? registryNameData?.join() || registryName : '',
          ciImageId: ciId?.join() || '',
          ciImageName: !isEmpty(ciId) ? ciNameData?.join() || ciImageName : '',
          clusterName:clusterName || '',
          hostClusterName: hostClusterName || '',
          codeRepoId: codeRepoId?.join() || '',
          codeRepoName: !isEmpty(codeRepoId) ? codeRepoData?.join() || codeRepoName : '',
          codeRepositoryId: codeRepositoryId?.join() || '',
          codeRepositoryName: !isEmpty(codeRepositoryId) ? codeRepositoryData?.join() || codeRepositoryName : '',
          //send the property received in config
          ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
        });
      }


    const fetchSubProductsAndEnvironments = useCallback(async (configurations) => {
        let subProductPromises = [];
        let envPromises = [];
        let subProducts = {};
        let envs = {};
        configurations.forEach(config => {
            subProductPromises.push(SubProductAPI.getAllQuick(config.product_id).promise);
            envPromises.push(SubProductEnvironmentTypeAPI.getAllQuick(config.sub_product_id).promise);
        });

        Promise.all(subProductPromises).then((result) => {
            result.forEach((entry, i) => {
                subProducts[configurations[i].product_id] = entry;
            });
            setSubProductsMap(subProducts);
        }).catch((err) => console.error(err));

        Promise.all(envPromises).then((result) => {
            result.forEach((entry, i) => {
                envs[`${configurations[i].product_id}-${configurations[i].sub_product_name}`] = entry;
            });
            setEnvMap(envs);
        }).catch((err) => console.error(err));
    }, []);


    const fetchProductSubproducts = useCallback(() => {
        setLoading(true);
        const { promise, cancel } = ProductAPI.getAllQuick();
        promise.then(async (_products) => {
            setProducts(_products);

            if (!editMode) {
                form.setFieldsValue({ configurations: props.formDefaultValues ? props.formDefaultValues : initialFormValues.configurations });
                setLoading(false);
            } else {
                if(props.record.product){
                    fetchSubProductsAndEnvironments([props.record]);
                }
                const { product_name } = props.record;
                const _productId = (find(_products, (obj) => obj.name === product_name))?.id;
                const extraFieldsData = parseExtraFieldsDataFromResponse(props.record);
                form.setFieldsValue({
                    configurations: [{
                        ...props.record,
                        ...extraFieldsData,
                        subProduct: props.record.sub_product_name,
                        product: _productId,
                    }]
                });
                setLoading(false);
            }
        }).catch(err => {
            console.error(err);
            setLoading(false);
        });
        return cancel;
    }, [form, editMode, fetchSubProductsAndEnvironments, props.record, props.formDefaultValues,parseExtraFieldsDataFromResponse]);

    useEffect(() => {
        return fetchProductSubproducts();
    }, [fetchProductSubproducts]);

    const fetchEnv = (subProductId, envMapKey) => {
        SubProductEnvironmentTypeAPI.getAllQuick(subProductId).promise.then(envs => {
            setEnvMap((envMap) => ({
                ...envMap,
                [envMapKey]: envs
            }));
        }).catch(err => console.error(err))
    }

    const onProductSubProductCreation = async (productSubProductData, field, type) => {
        const {name, id} = productSubProductData
        let newValues = form.getFieldValue("configurations") || {};
        const productId = newValues[field.name]?.product
        newValues[field.name] = {
            ...newValues[field.name],
            ...(type === 'product' ? { product: id, subProduct: undefined } : {}),
            ...(type === 'subProduct' ? { subProduct: name } : {}),
            environment: undefined
        }

        if(type === "product") {
            setProducts(products => ([...products, { id, name }]))
        }

        if(type === 'subProduct') {
            setSubProductsMap((subProductsMap) => ({
                ...subProductsMap,
                [productId]: [...(subProductsMap[productId] || []),{id, name}]
            }));
            fetchEnv(id, `${productId}-${name}`)
        }

        form.setFieldsValue(newValues);
    }


    const onChange = (value, field, type) => {
        const { name } = field;
        let newValues = form.getFieldValue("configurations") || [];
        if (newValues[name]) {
            newValues[name] = {
                ...newValues[name],
                ...(type === 'product' && { subProduct: undefined }),
                environment: undefined
            }

            if (type === 'product' && !subProductsMap[value]) {
                SubProductAPI.getAllQuick(value).promise.then((subproducts) => {
                    setSubProductsMap((subProductsMap) => ({
                        ...subProductsMap,
                        [value]: subproducts
                    }));
                }).catch(err => console.error(err));
            }

            const envMapKey = `${newValues[name].product}-${value}`;
            if (type === 'subProduct' && !envMap[envMapKey]) {
                const subId = find(subProductsMap[newValues[name].product], (sub) => sub.name === value).id;
                fetchEnv(subId, envMapKey);
            }
        }

        form.setFieldsValue(newValues);
    };

    const configureTool = async () => {
        await form.validateFields();
        
        try {
            setLoading(true);
            const data = form.getFieldsValue();
            const { configurations } = data;
            
            const newConfigurations = configurations.map(config => {
            const { product, subProduct, environment, id } = config;
            const _product = find(products, (obj) => obj.id === product);
            const extraFieldsData = getExtraFieldsData(config);
            return {
                ...extraFieldsData,
                product_name: _product.name,
                sub_product_name: subProduct,
                environment,
                tool_name: props.toolName,
                login_id: props.loginConfigId,
                id,
                toolType: IntegrationType.PULL
            }
        })

            if(editMode && !props.isUnmapped) {
                await ToolsIntegrationsAPI.updateGenericConfigurationByToolName(newConfigurations[0], false).promise;
            } else {
                await ToolsIntegrationsAPI.postGenericToolsConfiguration(newConfigurations, false).promise;
            }
            message.success('Configuration saved successfully');
            props.afterClose();
            props.onUpdateConfigComponent && props.onUpdateConfigComponent();
        } catch (e) {
            message.error(e.response?.data?.message || e.message || 'Please check the configurations again');
            console.error(e);
            setLoading(false);
            throw e;
        }
    };


    const _modalProps = {
        width: MODAL_SIZE_LARGE,
        style: { top: 80 },
        onOk: configureTool,
        onCancel: props.afterClose,
        title: `Map ${props.toolName} Projects`
    };

    return (
        <BaseModal
            modalProps={_modalProps}
            ref={ref}
            loading={loading}
        >
            <Form
                form={form}
                initialValues={initialFormValues.configurations}
            >
                    <ul style={{paddingLeft:15}}>
                        <li> Images can be mapped on ArmorCode from deployed, registries and CI from twistlock.</li>
                        <li>From cluster dropdown you can find unmapped twislock repository or hosts by selecting "Not mapped with cluster" </li>
                    </ul>

                <TwistLockFieldList
                    {...props}
                    editMode={editMode}
                    disableProjectFields={disableProjectFields}
                    commonProperty={commonTwistLockFieldsProperty}
                    name="configurations"
                    form={form}
                    setOptionsFromPagination={setOptionsFromPagination}
                    addButtonDisabled={props.disableAddConfiguration}
                    fieldRows={
                        [
                            {
                                name: 'product',
                                type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
                                colSpan: 8,
                                label: <>ArmorCode <Vocabulary name="product" isPlural={true} /></>,
                                props: {
                                    getFormListOptions: () => {
                                        return products.map(item => ({label: item.name, value: item.id}))
                                    },
                                    onChange: (value, field) => onChange(value, field, 'product'),
                                    dropdownType: 'product',
                                    showAddButton: true,
                                    dontFetchProductSubProduct: true,
                                    onCreate: (createdProduct, field) => {
                                        onProductSubProductCreation(createdProduct, field, 'product')
                                    }
                                },
                                rules: [{ required: true, message: <><Vocabulary name="product" isPlural={true} /> is required</> }],
                                disabled: disableMapping,
                                ...commonTwistLockFieldsProperty
                            },
                            {
                                name: 'subProduct',
                                type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
                                colSpan: 8,
                                label: <>ArmorCode <Vocabulary name="subProduct" isPlural={true} /></>,
                                props: {
                                    getFormListOptions: (fieldKey) => {
                                        const selectedProduct = form.getFieldValue("configurations")[fieldKey]?.product;
                                        return findOptionsFromMap(selectedProduct, subProductsMap);
                                    },
                                    getProductId: (listIndex) => {
                                        const configData = form.getFieldValue("configurations")
                                        return configData[listIndex]?.product
                                    },
                                    dontFetchProductSubProduct: true,
                                    dropdownType: 'subProduct',
                                    onCreate: (createdSubproduct, field) => {
                                        onProductSubProductCreation(createdSubproduct, field, 'subProduct')
                                    },
                                    onChange: (value, field) => onChange(value, field, 'subProduct')
                                },
                                rules: [{ required: true, message: <><Vocabulary name="subProduct" isPlural={true} /> is required</> }],
                                disabled: disableMapping,
                                ...commonTwistLockFieldsProperty
                            },
                            {
                                name: 'environment',
                                type: 'Select',
                                colSpan: 8,
                                label: 'ArmorCode Environment',
                                props: {
                                    getFormListOptions: (fieldKey) => {
                                        const selectedProduct = form.getFieldValue("configurations")[fieldKey]?.product;
                                        const selectedSubProduct = form.getFieldValue("configurations")[fieldKey]?.subProduct;
                                        return findOptionsFromMap(`${selectedProduct}-${selectedSubProduct}`, envMap, true);
                                    },
                                },
                                rules: [{ required: true, message: 'Environment is required' }],
                                disabled: disableMapping,
                                ...commonTwistLockFieldsProperty
                            }
                        ]
                    }
                />
            </Form>
        </BaseModal>
    )
})


const TwistLockFieldList = ({ commonProperty, name, form, editMode, deleteIconTooltip, addButtonDisabled, buttonLabel, fieldRows, fetchProjects,setOptionsFromPagination, setImageOptionsFromPagination, setHostOptionsFromPagination,disableProjectFields, ...props }) => {
    const commonFieldsProperty = useMemo(()=> ({
        ...commonProperty,
        disabled: disableProjectFields,
        props: {
            autoFocus: !disableProjectFields
        }
    }),[commonProperty, disableProjectFields])
    
    const clusterChange = useCallback((type,value, field) => {
        const { name } = field;
        const configurations = form.getFieldValue("configurations");
        let updatedConfiguration = [
            ...configurations
        ];

        updatedConfiguration[name] = {
            ...updatedConfiguration[name],
            ...(type === 'image' ?  { imageId: [] } : { hostId: [] })
        }
        form.setFieldsValue({ configurations: updatedConfiguration })
    }, [form])

    const fetchRepos = useCallback((params) => {
        const { promise,cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, params);
        promise.then((data) =>{
            setOptionsFromPagination(data.projects,params.type)
        })
        return { promise, cancel }
      }, [props.toolName,setOptionsFromPagination]);



    const appendAll = useCallback((list = []) => ([{ label: "Not mapped with cluster", value: null }, ...list]), []);

    const deployedFields = useMemo(() =>([
        {
            ...commonFieldsProperty,
            label: 'Cluster',
            name: "clusterName",
            colSpan: 12,
            type: FieldTypes.SELECT,
            layout: "vertical",
            help: 'If cluster name is specified, then vulnerabilities  related to image  deployed within cluster will be pulled.',
            props: {
                api:fetchRepos,
                apiParams: { login_id: props.loginConfigId, type:"image" },
                onChange: (value,field)=> clusterChange('image',value,field),
                parseSelectOptions: parseClusterSelectOption,
                optionsMutator: appendAll,
            },
        },
        {
            ...commonFieldsProperty,
            name: 'imageId',
            colSpan: 12,
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            label: 'Twistlock Image',
            props: {
                mode:"multiple",
                api:fetchRepos,
                apiParams: { login_id: props.loginConfigId, type:"image" },
                getFilterOptions: (fieldKey,options) => {
                    const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
                    const cluster = fieldConfiguration?.clusterName;
                    if (isNull(cluster)) {
                        return filter(options, (data) => isEmpty(data.otherProperties.clusters))
                    } else if(isUndefined(cluster)){
                        return options;
                    }
                    else {
                        return filter(options, (data) => data.otherProperties.clusters?.includes(cluster))
                    }
                },
            },
        }
    ]),[appendAll, clusterChange, commonFieldsProperty, fetchRepos, form,props.loginConfigId]);

    const hostFields = useMemo(() => ([
        {
            ...commonFieldsProperty,
            label: 'Host Cluster',
            name: "hostClusterName",
            colSpan: 10,
            type: FieldTypes.SELECT,
            layout: "vertical",
            help: 'If cluster name is specified, then vulnerabilities  related to image  deployed within cluster will be pulled.',
            props: {
                onChange: (value,field)=> clusterChange('host',value,field),
                api:fetchRepos,
                apiParams: { login_id: props.loginConfigId, type:"host" },
                parseSelectOptions: parseClusterSelectOption,
                optionsMutator: appendAll,
            },
        },
        {
            ...commonFieldsProperty,
            name: 'hostId',
            colSpan: 12,
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            label: 'Hostname',
            props: {
                mode:"multiple",
                api:fetchRepos,
                apiParams: { login_id: props.loginConfigId, type:"host" },
                getFilterOptions: (fieldKey,options) => {
                    const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
                    const cluster = fieldConfiguration?.hostClusterName;
                    if (isNull(cluster)) {
                        return options?.filter(host => isEmpty(host.otherProperties.clusters))
                    } else if(isUndefined(cluster)){
                        return options;
                    }
                    else {
                        return options?.filter(host => host.otherProperties.clusters === cluster);
                    }
                },
                setExtraProps: (field, listField) => {
                    const { name } = listField;
                    const config = form.getFieldValue("configurations")[name];
                    return {
                        disabled: config?.allHosts
                    }
                }
            },
        },
        {
            ...commonFieldsProperty,
            label: "All",
            name: "allHosts",
            colSpan: 2,
            type: FieldTypes.BOOLEAN,
            props: {
                onChange: (e) => {
                    const { name } = e.target.listField;
                    const configurations = form.getFieldValue("configurations");
                    let updatedConfiguration = [
                        ...configurations
                    ];
                    updatedConfiguration[name] = {
                        ...updatedConfiguration[name],
                        hostId: []   
                    }
                    form.setFieldsValue({ configurations: updatedConfiguration })
                }
            }
        }
    ]), [commonFieldsProperty, clusterChange,fetchRepos,props.loginConfigId,appendAll,form]);


    const registryFields = useMemo(() => ([
        {
            ...commonFieldsProperty,
            name: 'registryId',
            colSpan: 12,
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            label: 'Repository',
            props: {
                mode:"multiple",
                api:fetchRepos,
                apiParams: { login_id: props.loginConfigId, type:"registry" },
            }
        }
    ]), [commonFieldsProperty,fetchRepos,props.loginConfigId]);

    const ciFields = useMemo(() => ([
        {
            ...commonFieldsProperty,
            name: 'ciId',
            colSpan: 12,
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            label: 'Repository',
            help: 'A scheduled job is run every 24 hours to fetch this data.',
            props: {
                mode:"multiple",
                api:fetchRepos,
                apiParams: { login_id: props.loginConfigId, type:"ci" },
            }

        }
    ]), [commonFieldsProperty,fetchRepos,props.loginConfigId]);

    const codeFields = useMemo(() => ([
        {
            ...commonFieldsProperty,
            name: 'codeRepoId',
            colSpan: 12,
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            label: 'Project Name',
            props: {
                mode:"multiple",
                api:fetchRepos,
                apiParams: { login_id: props.loginConfigId, type:"code" },
            }
        }
    ]), [commonFieldsProperty,fetchRepos,props.loginConfigId]);

    // const codeRepoFields = useMemo(() => ([
    //     {
    //         ...commonFieldsProperty,
    //         name: 'codeRepositoryId',
    //         colSpan: 12,
    //         type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
    //         label: 'Code Repository Name',
    //         props: {
    //             mode:"multiple",
    //             api:fetchRepos,
    //             apiParams: { login_id: props.loginConfigId, type:"codeRepo" },
    //         }
    //     }
    // ]), [commonFieldsProperty,fetchRepos,props.loginConfigId]);

    return <Form.List name={name}>
        {(fields, { add, remove }) => (
            <>
                {fields.map((currentRowField) => {
                    return <Alert
                    className="m-b-sm"
                    key={currentRowField.key}
                    type="info"
                    description={<>
                            <Tabs defaultActiveKey="1">
                                <TabPane tab="Images" key="image">
                                    <Tabs defaultActiveKey='deployed'>
                                        <TabPane tab="Deployed" key="image" >
                                            {generateFormFields(deployedFields, 24, currentRowField)}
                                        </TabPane>
                                        <TabPane tab="Registry" key="registry">
                                            {generateFormFields(registryFields, 24, currentRowField)}
                                        </TabPane>
                                        <TabPane tab="CI" key="ci">
                                            {generateFormFields(ciFields, 24, currentRowField)}
                                        </TabPane>
                                    </Tabs>
                                </TabPane>
                                <TabPane tab="Host" key="host">
                                    <Alert type="warning" className="m-b-sm"  message="This config will take time to load." />
                                    {generateFormFields(hostFields, 24, currentRowField)}
                                </TabPane>
                                <TabPane tab="Application Security" key="code">
                                    {generateFormFields(codeFields, 24, currentRowField)}
                                </TabPane>
                                {/* <TabPane tab="Code Repositories" key="codeRepo">
                                    {generateFormFields(codeRepoFields, 24, currentRowField)}
                                </TabPane> */}
                            </Tabs>

                            <div className="d-flex flex-column align-items-center">
                                <Typography.Text>Map To</Typography.Text>
                                <ArrowDownOutlined className='p-xs' />
                            </div>

                            {generateFormFields(fieldRows, 24, currentRowField)}

                            {!editMode &&
                                <div span={24} className="m-b-0 text-right" >
                                    <Button type="dashed" 
                                        className={classNames({ disabled: fields.length === 1 }, 'danger')} 
                                        icon={<DeleteIcon/>} disabled={addButtonDisabled} 
                                        onClick={() => {
                                            if (fields.length !== 1) {
                                               remove(currentRowField.name);
                                            }
                                    }} >
                                        {buttonLabel || "Delete Row"}
                                    </Button>
                                </div>
                            }
                        </>}
                    />
                })}
                {!editMode && 
                    <div span={24} className="m-b-0 p-l-xs p-r-xs text-right" >
                        <Button 
                            type="dashed" 
                            onClick={() => add()} 
                            icon={<PlusOutlined />} 
                            disabled={addButtonDisabled}
                        >
                            {buttonLabel || "Add Mapping"}
                        </Button>
                    </div>
                }
            </>
        )}
    </Form.List>
}

const parseGroupSelectOption = (options,key) => {

    let groupKeyOptions = {}
    let clusters = []

    if(isEmpty(options)){
        groupKeyOptions[key] = [];
    }
    else{
        map(options, option => {
            const { label = option.name, value = option.id, otherProperties, cluster = option.otherProperties.clusters } = option || {};
            if (groupKeyOptions[otherProperties.type]) {
                groupKeyOptions[otherProperties.type].push({ label, value, cluster })
            } else {
                groupKeyOptions[otherProperties.type] = [{ label, value, cluster }]
            }
    
            if (!isEmpty(cluster)) {
                isArray(cluster) ? clusters.push(...cluster) : clusters.push(cluster);
            }
        })
    
        clusters = map([...new Set(clusters)], cluster => ({
            label: capitalize(cluster), value: cluster
        }))
    }
    return { project: groupKeyOptions, clusters }

}

export default TwistLockMappingFormV2;

export const parseClusterSelectOption = (options) => {
        
    const cluster = map(options.projects, option => {
        const {otherProperties:{clusters}} = option;
       return clusters
    });

    return [...new Set(flatMapDeep(cluster))].filter((data)=>Boolean(data)).map(cluster => ({
        label: capitalize(cluster), value: cluster
    }))
}

